import { createContext, useContext, useEffect, useRef, useState } from "react";
import supabase from "../lib/supabaseClient";

const AuthContext = createContext(null);

// Auto sign-out after this much inactivity (and on reopening a stale tab).
export const IDLE_LIMIT_MS = 15 * 60 * 1000; // 15 minutes
const LAST_ACTIVITY_KEY = "cv_last_activity";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const idleTimer = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const session = data.session;
      if (session) {
        // If the tab was closed and reopened after the idle window, force logout.
        const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
        if (last && Date.now() - last > IDLE_LIMIT_MS) {
          supabase.auth.signOut();
          localStorage.removeItem(LAST_ACTIVITY_KEY);
          setUser(null);
        } else {
          setUser(session.user);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null);
    });
    return () => listener.subscription.unsubscribe();
  }, []);

  // Inactivity watchdog: reset on user activity, sign out when it elapses.
  useEffect(() => {
    if (!user) {
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      return;
    }
    const arm = () => {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        supabase.auth.signOut();
        localStorage.removeItem(LAST_ACTIVITY_KEY);
      }, IDLE_LIMIT_MS);
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, arm, { passive: true }));
    // Re-check staleness when the tab becomes visible again.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
      if (last && Date.now() - last > IDLE_LIMIT_MS) supabase.auth.signOut();
      else arm();
    };
    document.addEventListener("visibilitychange", onVisible);
    arm();
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach((e) => window.removeEventListener(e, arm));
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  const signIn = (email, password) =>
    supabase.auth.signInWithPassword({ email, password });
  const signOut = () => supabase.auth.signOut();

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
